import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  Shield,
  Eye,
  Zap,
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Scale,
  HelpCircle,
  BookOpen,
} from 'lucide-react';
import { HierarchicalGovernanceChart } from './HierarchicalGovernanceChart';
import CollapsibleSection from './CollapsibleSection';

export function LegacyComparison({ cityName }: { cityName: string }) {
  const [activeScenario, setActiveScenario] = useState('budget');

  return (
    <section
      id="governance"
      className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-background to-primary/5"
    >
      <div className="container px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-6 xs:space-y-8">
            {/* Section Header */}
            <div className="text-center space-y-3 xs:space-y-4">
              <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                {cityName} Community Governance Framework
              </h2>
              <p className="text-base xs:text-lg sm:text-xl text-muted-foreground max-w-4xl mx-auto sm:px-6">
                This isn't a rigid system, it's a configurable digital framework that adapts to
                whatever governance model your community chooses. Every community can customize how
                decisions flow, who has override authority, and whether the community votes directly
                or through representatives.
              </p>
              <div className="bg-blue-50 border border-blue-200 p-3 xs:p-4 rounded-lg max-w-3xl mx-auto">
                <p className="text-sm text-blue-800">
                  <strong>Malleable by Design:</strong> Your community can choose from multiple
                  governance models Community Elect as overrider, community as override authority,
                  full popular vote systems, or hybrid approaches. The technology adapts to your
                  community's preferences.
                </p>
              </div>
            </div>

            {/* Hierarchical Governance Chart */}
            <HierarchicalGovernanceChart cityName={cityName} />

            {/* Accountability & Override System */}
            <CollapsibleSection
              title={
                <>
                  <Shield className="w-5 h-5 text-amber-600" />
                  What If Someone Becomes Tyrannical?
                </>
              }
              className="border-2 border-amber-200 bg-amber-50/50"
            >
              <div className="space-y-6">
                <p className="text-base text-muted-foreground">
                  The most important question: <strong>What stops abuse of power?</strong> The
                  community always has the ultimate power to override anyone, and each layer can
                  override the layers below it.
                </p>

                {/* Layered Override Structure - Top Down */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-center">
                    Governance Hierarchy (Top-Down Structure, Bottom-Up Accountability)
                  </h4>
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Higher layers can override lower layers. The community can override everyone.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {/* Left Column: Hierarchy */}
                    <div className="lg:col-span-2 space-y-3">
                      {/* Community - Ultimate Power */}
                      <Card className="bg-green-50 border-green-300 border-2 shadow-lg">
                        <CardContent className="p-5">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                              <Users className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <div className="font-bold text-green-900 mb-1 text-lg">
                                THE COMMUNITY (All Residents)
                              </div>
                              <p className="text-sm text-green-800 mb-2 font-semibold">
                                ULTIMATE POWER - Cannot be overridden
                              </p>
                              <div className="bg-green-100 border border-green-300 rounded p-3 space-y-2">
                                <div className="text-sm text-green-800">
                                  <strong>Nuclear Option:</strong> If 2/3 majority of authenticated
                                  resident app users vote "yes", the Community Elect (or any elected
                                  official) is immediately removed.
                                </div>
                                <div className="text-sm text-green-800">
                                  <strong>Transition Team:</strong> A specialized Transition
                                  Department (elected observers who stay informed but don't make
                                  daily decisions) immediately steps in to fill the vacancy. The
                                  Transition Department has the capacity to replace an entire
                                  subpanel if needed, not just single roles. The Transition Team
                                  selects temporary replacements based on their operational
                                  knowledge, maintaining governance continuity until the official
                                  election completes (90+ days: 30-day registration, 30-day
                                  campaign, 30+ day debates). Acting officials can choose to run in
                                  the permanent election during Month 1 registration.
                                </div>
                                <div className="text-sm text-green-700 italic">
                                  Why Transition Team? Prevents cascading vacancies and double
                                  voting. Residents already voted to remove; Transition Team's
                                  expertise ensures the right temporary replacement without another
                                  election.
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Arrow Down */}
                      <div className="flex justify-center items-center gap-2">
                        <div className="text-3xl text-green-600 font-bold">↓</div>
                        <div className="text-sm font-semibold text-green-600">Can Override</div>
                      </div>

                      {/* Layer 0: Community Elect */}
                      <Card className="bg-yellow-50 border-yellow-300 border-2">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                              0
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-yellow-900 mb-1">
                                Layer 0: Community Elect
                              </div>
                              <p className="text-sm text-yellow-800 mb-2">
                                Highest governance authority - stays behind the fray, overrides when
                                needed
                              </p>
                              <div className="flex items-center gap-2 text-xs bg-yellow-100 border border-yellow-300 rounded px-2 py-1">
                                <AlertTriangle className="w-3 h-3 text-amber-600" />
                                <span className="text-yellow-700">
                                  Can override all Layer 1 departments
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Arrow Down */}
                      <div className="flex justify-center items-center gap-2">
                        <div className="text-2xl text-yellow-600">↓</div>
                        <div className="text-xs text-yellow-600">Can Override</div>
                      </div>

                      {/* Layer 1: Community Department Elects */}
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                              1
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-purple-900 mb-1">
                                Layer 1: Community Department Elects
                              </div>
                              <p className="text-sm text-purple-800 mb-2">
                                Examples: Finance Elect, Roads Elect, Legal Elect
                              </p>
                              <div className="flex items-center gap-2 text-xs bg-purple-100 border border-purple-300 rounded px-2 py-1">
                                <AlertTriangle className="w-3 h-3 text-amber-600" />
                                <span className="text-purple-700">
                                  Can override Layer 2 sub-departments (if any exist)
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Arrow Down */}
                      <div className="flex justify-center items-center gap-2">
                        <div className="text-2xl text-purple-600">↓</div>
                        <div className="text-xs text-purple-600">Can Override (if exists)</div>
                      </div>

                      {/* Layer 2: Sub-Department Elects */}
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0 text-lg">
                              2
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-blue-900 mb-1">
                                Layer 2: Sub-Department Elects (Optional)
                              </div>
                              <p className="text-sm text-blue-800 mb-2">
                                Example: Roads → Snowplow Operations Elect
                              </p>
                              <div className="flex items-center gap-2 text-xs bg-blue-100 border border-blue-300 rounded px-2 py-1">
                                <AlertTriangle className="w-3 h-3 text-amber-600" />
                                <span className="text-blue-700">
                                  Lowest layer - manages specific operations
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Right Column: Key Points */}
                    <div className="space-y-3">
                      <div className="bg-white border-2 border-green-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Users className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-sm sm:text-base mb-1">
                              Community = Ultimate Power
                            </div>
                            <div className="text-sm text-muted-foreground">
                              No single person can become a permanent dictator. Popular vote
                              overrides anyone.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-amber-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Zap className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-sm sm:text-base mb-1">
                              Fast Removal
                            </div>
                            <div className="text-sm text-muted-foreground">
                              If a Community Elect abuses power, residents trigger removal vote.
                              Threshold met = immediate removal.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-sm sm:text-base mb-1">
                              Layered Accountability
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Higher layers override lower layers. Layer 0 overrides Layer 1. Layer
                              1 overrides Layer 2.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-purple-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Eye className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-semibold text-sm sm:text-base mb-1">
                              100% Transparent
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Every override, vote, and dispute is recorded and visible. No backroom
                              deals.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-yellow-50 border-2 border-green-300 rounded-lg p-3">
                        <div className="text-center">
                          <div className="text-sm font-semibold text-green-800 mb-2">
                            Example Scenario:
                          </div>
                          <div className="text-sm text-muted-foreground leading-relaxed">
                            Community Elect (Layer 0) starts making tyrannical decisions → 2/3 of
                            residents vote to remove → Community Elect immediately removed →
                            Transition Team steps in → New election begins
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Bridge Section */}
            <CollapsibleSection
              title={
                <>
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  Transform {cityName} Decision-Making
                </>
              }
              className="space-y-3 xs:space-y-4"
            >
              <div className="pt-6 xs:pt-8 text-center sm:px-6">
                <p className="text-base xs:text-lg text-muted-foreground max-w-3xl mx-auto">
                  Stop accepting slow, frustrating governance as "just how communities work." See
                  how subpanel systems resolve issues faster, with higher satisfaction, and complete
                  transparency while maintaining the digital democratic voice you want.
                </p>

                {/* Comparison Tabs */}
                <Tabs defaultValue="structure" className="space-y-4 xs:space-y-6 mt-6">
                  <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-0.5 sm:gap-0 h-auto sm:h-10">
                    <TabsTrigger
                      value="structure"
                      className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5"
                    >
                      Structure
                    </TabsTrigger>
                    <TabsTrigger
                      value="decisions"
                      className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5"
                    >
                      <span className="hidden sm:inline">Decision Making</span>
                      <span className="sm:hidden">Decisions</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="transparency"
                      className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5"
                    >
                      <span className="hidden sm:inline">Transparency</span>
                      <span className="sm:hidden">Open</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="scenarios"
                      className="text-xs sm:text-sm px-2 py-2 sm:px-3 sm:py-1.5"
                    >
                      <span className="hidden sm:inline">Real Scenarios</span>
                      <span className="sm:hidden">Examples</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="structure">
                    <StructureComparison />
                  </TabsContent>

                  <TabsContent value="decisions">
                    <DecisionMakingComparison />
                  </TabsContent>

                  <TabsContent value="transparency">
                    <TransparencyComparison />
                  </TabsContent>

                  <TabsContent value="scenarios">
                    <ScenarioComparison
                      activeScenario={activeScenario}
                      onScenarioChange={setActiveScenario}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </CollapsibleSection>

            {/* Call to Action (commented out) - removed from render to streamline landing
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="text-center p-4 xs:p-6 sm:p-8">
              <h3 className="text-xl xs:text-2xl font-bold mb-3 xs:mb-4">
                Ready to See {cityName}' Transformation?
              </h3>
              <p className="text-sm xs:text-base text-muted-foreground mb-4 xs:mb-6 max-w-2xl mx-auto">
                Every community is different. See how the subpanel system would work with {cityName}' specific governance preferences and the potential improvements in decision speed, transparency, and resident satisfaction.
              </p>
            </CardContent>
          </Card>
      */}

            {/* Call to Action (commented out) - removed from render to streamline landing
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="text-center p-4 xs:p-6 sm:p-8">
              <h3 className="text-xl xs:text-2xl font-bold mb-3 xs:mb-4">
                Ready to See {cityName}' Transformation?
              </h3>
              <p className="text-sm xs:text-base text-muted-foreground mb-4 xs:mb-6 max-w-2xl mx-auto">
                Every community is different. See how the subpanel system would work with {cityName}' specific governance preferences and the potential improvements in decision speed, transparency, and resident satisfaction.
              </p>
            </CardContent>
          </Card>
      */}
          </div>
        </div>
      </div>
    </section>
  );
}

function StructureComparison() {
  const comparisons = [
    {
      aspect: 'Leadership Structure',
      legacy: {
        text: 'Unclear roles, power struggles',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: '1 Community Elect (overrider) + max 3 Community Directors (decision-makers)',
        icon: CheckCircle,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Decision Making',
      legacy: { text: 'Committee meetings, endless debates', icon: Clock, color: 'text-red-600' },
      modern: {
        text: 'Directors propose, Community Elect votes, authenticated resident app users can override',
        icon: Zap,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Override Authority',
      legacy: {
        text: 'Single leader makes unilateral decisions',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'Community Elect stays behind the fray, overrides only when Community Directors or residents request',
        icon: Shield,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Community Voice',
      legacy: {
        text: 'Annual meetings, limited input',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'Can vote out elected leaders OR request digital override',
        icon: Users,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Transparency',
      legacy: {
        text: 'Closed meetings, summary minutes',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'All decisions tracked to individuals in real-time',
        icon: Eye,
        color: 'text-green-600',
      },
    },
    {
      aspect: 'Governance Model',
      legacy: {
        text: 'Fixed structure, one-size-fits-all',
        icon: AlertTriangle,
        color: 'text-red-600',
      },
      modern: {
        text: 'Configurable - choose your preferred decision-making model',
        icon: Scale,
        color: 'text-green-600',
      },
    },
  ];

  return (
    <div className="grid gap-3 xs:gap-4">
      {comparisons.map((comp, index) => (
        <Card key={index}>
          <CardContent className="p-3 xs:p-4 sm:p-6">
            <div className="grid lg:grid-cols-3 gap-3 xs:gap-4 items-start">
              <div className="font-semibold text-base xs:text-lg mb-2 lg:mb-0">{comp.aspect}</div>

              <div className="flex items-start gap-2 xs:gap-3 p-3 xs:p-4 rounded-lg bg-red-50 border border-red-200">
                <comp.legacy.icon
                  className={`w-4 h-4 xs:w-5 xs:h-5 mt-0.5 ${comp.legacy.color} flex-shrink-0`}
                />
                <div>
                  <div className="text-xs xs:text-sm font-medium text-red-800 mb-1">
                    Traditional Approach
                  </div>
                  <div className="text-xs xs:text-sm text-red-700">{comp.legacy.text}</div>
                </div>
              </div>

              <div className="flex items-start gap-2 xs:gap-3 p-3 xs:p-4 rounded-lg bg-green-50 border border-green-200">
                <comp.modern.icon
                  className={`w-4 h-4 xs:w-5 xs:h-5 mt-0.5 ${comp.modern.color} flex-shrink-0`}
                />
                <div>
                  <div className="text-xs xs:text-sm font-medium text-green-800 mb-1">
                    Subpanel System
                  </div>
                  <div className="text-xs xs:text-sm text-green-700">{comp.modern.text}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DecisionMakingComparison() {
  const processes: Array<{
    scenario: string;
    legacy: {
      steps: string[];
      time: string;
      satisfaction: string;
    };
    modern: {
      steps: string[];
      time: string;
      satisfaction: string;
      note?: string;
    };
  }> = [
    {
      scenario: 'Community Infrastructure Approval',
      legacy: {
        steps: [
          'Community complaint filed',
          'Review scheduled (next month)',
          'Committee assessment delays',
          'Debates over contractor selection',
          'Resident notification of decision',
        ],
        time: '3-4 months',
        satisfaction: '35%',
      },
      modern: {
        steps: [
          'Community voices infrastructure need',
          'Community Directors reviews request',
          'Community Directors creates resolution',
          'Community Directors votes on resolution',
          'Community can override if disagreed*',
        ],
        time: '2-3 weeks',
        satisfaction: '88%',
        note: '*Authenticated resident app users can either vote out elected Community Directors or Community Department Directors or request Community Elect override via digital vote',
      },
    },
    {
      scenario: 'Budget Approval',
      legacy: {
        steps: [
          '3-month committee review',
          'Meeting debates',
          'Resident vote',
          'Implementation delays',
        ],
        time: '5 months',
        satisfaction: '40%',
      },
      modern: {
        steps: [
          'Community requests budget changes',
          'Community Directors reviews needs',
          'Community Directors create budget resolution',
          'Community Directors votes on resolution',
          'Community override options available*',
        ],
        time: '5 weeks',
        satisfaction: '85%',
        note: '*Community Elect stays behind the fray - only overrides when authenticated resident app users request it',
      },
    },
    {
      scenario: 'Maintenance Issue',
      legacy: {
        steps: [
          'Resident complaint',
          'Manager review',
          'Review process',
          'Vendor selection delays',
        ],
        time: '6-8 weeks',
        satisfaction: '50%',
      },
      modern: {
        steps: [
          'Digital report filed',
          'Community Directors reviews issue',
          'Community Directors assesses & proposes solution',
          'Community Directors votes, Community Elect available for disputes*',
        ],
        time: '1-2 weeks',
        satisfaction: '90%',
        note: '*Community Elect only intervenes when residents request override',
      },
    },
  ];

  return (
    <div className="space-y-4 xs:space-y-6">
      {processes.map((process, index) => (
        <Card key={index}>
          <CardHeader className="p-3 xs:p-4 sm:p-6">
            <CardTitle className="text-base xs:text-lg">{process.scenario}</CardTitle>
          </CardHeader>
          <CardContent className="p-3 xs:p-4 sm:p-6 pt-0">
            <div className="grid lg:grid-cols-2 gap-4 xs:gap-6">
              {/* Legacy Process */}
              <div className="space-y-3 xs:space-y-4">
                <div className="flex items-center gap-2 mb-3 xs:mb-4">
                  <Badge className="bg-red-100 text-red-800 border-red-200 text-xs xs:text-sm">
                    🔴 Traditional Approach
                  </Badge>
                </div>

                <div className="space-y-2 xs:space-y-3">
                  {process.legacy.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex items-start gap-2 xs:gap-3">
                      <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-red-100 text-red-600 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                        {stepIndex + 1}
                      </div>
                      <div className="text-xs xs:text-sm">{step}</div>
                    </div>
                  ))}
                </div>

                <div className="pt-3 xs:pt-4 border-t flex flex-col xs:flex-row xs:justify-between gap-2 text-xs xs:text-sm">
                  <div>
                    <span className="font-medium">Time:</span> {process.legacy.time}
                  </div>
                  <div>
                    <span className="font-medium">Satisfaction:</span> {process.legacy.satisfaction}
                  </div>
                </div>
              </div>

              {/* Modern Process */}
              <div className="space-y-3 xs:space-y-4">
                <div className="flex items-center gap-2 mb-3 xs:mb-4">
                  <Badge className="bg-green-100 text-green-800 border-green-200 text-xs xs:text-sm">
                    🟢 Subpanel System
                  </Badge>
                </div>

                <div className="space-y-2 xs:space-y-3">
                  {process.modern.steps.map((step, stepIndex) => (
                    <div key={stepIndex} className="flex items-start gap-2 xs:gap-3">
                      <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-green-100 text-green-600 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                        {stepIndex + 1}
                      </div>
                      <div className="text-xs xs:text-sm">{step}</div>
                    </div>
                  ))}
                </div>

                {process.modern.note && (
                  <div className="text-xs text-green-700 bg-green-50 p-2 rounded border border-green-200">
                    {process.modern.note}
                  </div>
                )}

                <div className="pt-3 xs:pt-4 border-t flex flex-col xs:flex-row xs:justify-between gap-2 text-xs xs:text-sm">
                  <div>
                    <span className="font-medium">Time:</span> {process.modern.time}
                  </div>
                  <div>
                    <span className="font-medium">Satisfaction:</span> {process.modern.satisfaction}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TransparencyComparison() {
  const features = [
    {
      feature: 'Meeting Records',
      legacy: 'Summary minutes, weeks later',
      modern: 'Real-time decision logging',
      improvement: 'Instant access',
    },
    {
      feature: 'Financial Transparency',
      legacy: 'Quarterly reports',
      modern: 'Live budget tracking',
      improvement: 'Always up-to-date',
    },
    {
      feature: 'Voting Records',
      legacy: '"The group voted 4-1"',
      modern: 'Individual voting history available',
      improvement: 'Full accountability',
    },
    {
      feature: 'Decision Rationale',
      legacy: 'Rarely documented',
      modern: 'Required explanation for every choice',
      improvement: 'No more guessing',
    },
    {
      feature: 'Community Input',
      legacy: 'Occasional meetings only',
      modern: 'Digital feedback anytime',
      improvement: 'Voice heard daily',
    },
    {
      feature: 'Appeal Process',
      legacy: 'Unclear, political',
      modern: 'Clear escalation path',
      improvement: 'Fair resolution',
    },
  ];

  return (
    <div className="space-y-3 xs:space-y-4">
      {features.map((feature, index) => (
        <Card key={index}>
          <CardContent className="p-3 xs:p-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 xs:gap-4 items-start">
              <div className="font-semibold text-sm xs:text-base mb-2 lg:mb-0">
                {feature.feature}
              </div>

              <div className="text-xs xs:text-sm text-red-700 bg-red-50 p-2 xs:p-3 rounded border border-red-200">
                <div className="font-medium text-red-800 mb-1">Legacy</div>
                {feature.legacy}
              </div>

              <div className="text-xs xs:text-sm text-green-700 bg-green-50 p-2 xs:p-3 rounded border border-green-200">
                <div className="font-medium text-green-800 mb-1">Modern</div>
                {feature.modern}
              </div>

              <div className="text-center lg:text-center">
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                  {feature.improvement}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ScenarioComparison({
  activeScenario,
  onScenarioChange,
}: {
  activeScenario: string;
  onScenarioChange: (scenario: string) => void;
}) {
  const scenarios = {
    budget: {
      title: 'Annual Budget Process',
      legacy: {
        timeline: '20 weeks',
        result: '5-month process, community divisiveness, rushed implementation',
        steps: [
          'Week 1-4: Budget committee meetings (4 times, disagreements)',
          'Week 5-8: Revised proposals, more committee debates',
          'Week 9: Meeting #1 - sends budget back to committee',
          'Week 10-12: More revisions, political maneuvering',
          'Week 13: Meeting #2 - narrow approval (3-2 vote)',
          'Week 14-16: Community notification, resident complaints',
          'Week 17: Resident meeting - challenges and appeals',
          'Week 18-20: Final revisions and approval',
        ],
      },
      modern: {
        timeline: '5 weeks',
        result: '5-week process, community confidence, smooth implementation',
        steps: [
          'Week 1: Community voices budget concerns and priorities',
          'Week 2: Community Directors review and discuss options',
          'Week 3: Community Directors draft budget resolution based on community input',
          'Week 4: Community Directors vote on resolution (Community Elect stays neutral)',
          'Week 5: Implementation begins with community override options available*',
        ],
      },
    },
    governance: {
      title: 'Choosing Your Governance Model',
      legacy: {
        timeline: 'Fixed forever',
        result: 'Stuck with inherited structure whether it works or not',
        steps: [
          'Community inherits decades-old bylaws',
          "Structure doesn't fit community needs",
          'No flexibility to adapt or improve',
          'Resident frustration grows over time',
          'Major bylaw changes require expensive legal process',
        ],
      },
      modern: {
        timeline: 'Configurable anytime',
        result: 'Community chooses and can adapt governance to their preferences',
        steps: [
          'Authenticated resident app users select preferred decision-making model',
          'Options: Community Elect override, community override, popular vote',
          "System adapts to community's chosen governance style",
          'Can switch models based on authenticated resident app user feedback',
          'Technology handles the complexity automatically*',
        ],
      },
    },
    emergency: {
      title: 'Emergency Infrastructure Response',
      legacy: {
        timeline: 'Weeks of delay',
        result: 'Residents affected, community anger, expensive emergency repairs',
        steps: [
          'Major issue affects multiple properties',
          'Affected residents try to reach leadership',
          'Leadership says "need to call emergency meeting"',
          'Leaders unavailable - some out of town, others at work',
          'Takes days to coordinate meeting',
          'Debate over contractor selection and who pays',
          'Residents deal with ongoing issues meanwhile',
        ],
      },
      modern: {
        timeline: 'Same day',
        result: 'Issue resolved quickly, community confidence maintained',
        steps: [
          'Issue reported through digital emergency system',
          'Community Directors automatically notified',
          'Community Directors activates pre-approved emergency authority',
          'Contractor contacted immediately using pre-vetted vendor list',
          'If Community Directors stalls: Authenticated resident app users can override & replace within 24 hours*',
          'All members notified with repair timeline and cost allocation',
        ],
      },
    },
  };

  const scenarioData = scenarios[activeScenario as keyof typeof scenarios];

  return (
    <div className="space-y-4 xs:space-y-6">
      <div className="flex gap-1 xs:gap-2 justify-center flex-wrap px-2">
        <Button
          variant={activeScenario === 'budget' ? 'default' : 'outline'}
          onClick={() => onScenarioChange('budget')}
          size="sm"
          className="text-xs xs:text-sm"
        >
          Budget Process
        </Button>
        <Button
          variant={activeScenario === 'governance' ? 'default' : 'outline'}
          onClick={() => onScenarioChange('governance')}
          size="sm"
          className="text-xs xs:text-sm"
        >
          <span className="hidden xs:inline">Governance Models</span>
          <span className="xs:hidden">Models</span>
        </Button>
        <Button
          variant={activeScenario === 'emergency' ? 'default' : 'outline'}
          onClick={() => onScenarioChange('emergency')}
          size="sm"
          className="text-xs xs:text-sm"
        >
          <span className="hidden xs:inline">Emergency Response</span>
          <span className="xs:hidden">Emergency</span>
        </Button>
      </div>

      <Card>
        <CardHeader className="p-3 xs:p-4 sm:p-6">
          <CardTitle className="text-lg xs:text-xl text-center">{scenarioData.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-3 xs:p-4 sm:p-6 pt-0">
          <div className="grid lg:grid-cols-2 gap-4 xs:gap-6 sm:gap-8">
            {/* Legacy Process */}
            <div className="space-y-3 xs:space-y-4">
              <div className="text-center">
                <Badge className="bg-red-100 text-red-800 border-red-200 mb-3 xs:mb-4 text-xs xs:text-sm">
                  🔴 Traditional Approach
                </Badge>
                <div className="text-xl xs:text-2xl font-bold text-red-600 mb-2">
                  {scenarioData.legacy.timeline}
                </div>
              </div>

              <div className="space-y-2 xs:space-y-3">
                {scenarioData.legacy.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 xs:gap-3 p-2 xs:p-3 rounded bg-red-50 border border-red-200"
                  >
                    <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <div className="text-xs xs:text-sm text-red-800">{step}</div>
                  </div>
                ))}
              </div>

              <div className="p-3 xs:p-4 bg-red-100 border border-red-200 rounded">
                <div className="font-semibold text-red-800 mb-2 text-sm xs:text-base">Result:</div>
                <div className="text-xs xs:text-sm text-red-700">{scenarioData.legacy.result}</div>
              </div>
            </div>

            {/* Modern Process */}
            <div className="space-y-3 xs:space-y-4">
              <div className="text-center">
                <Badge className="bg-green-100 text-green-800 border-green-200 mb-3 xs:mb-4 text-xs xs:text-sm">
                  🟢 Subpanel System Process
                </Badge>
                <div className="text-xl xs:text-2xl font-bold text-green-600 mb-2">
                  {scenarioData.modern.timeline}
                </div>
              </div>

              <div className="space-y-2 xs:space-y-3">
                {scenarioData.modern.steps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 xs:gap-3 p-2 xs:p-3 rounded bg-green-50 border border-green-200"
                  >
                    <div className="w-5 h-5 xs:w-6 xs:h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <div className="text-xs xs:text-sm text-green-800">{step}</div>
                  </div>
                ))}
              </div>

              <div className="p-3 xs:p-4 bg-green-100 border border-green-200 rounded">
                <div className="font-semibold text-green-800 mb-2 text-sm xs:text-base">
                  Result:
                </div>
                <div className="text-xs xs:text-sm text-green-700">
                  {scenarioData.modern.result}
                </div>
                {activeScenario === 'governance' && (
                  <div className="mt-3 text-xs text-green-600 bg-green-50 p-2 rounded">
                    <strong>Available Models:</strong> Some communities prefer Community Elect as
                    final overrider, others want Community SubPanel as typical voting decision
                    maker, and some choose community popular vote where Community SubPanel drafts
                    and community decides. The system adapts to your preference.
                  </div>
                )}
                {activeScenario === 'emergency' && (
                  <div className="mt-3 text-xs text-green-600 bg-green-50 p-2 rounded">
                    <strong>Community Override Power:</strong> If a Community Director fails to act
                    on an approved emergency repair, the community can digitally vote to remove them
                    and elect a replacement within 24 hours. This ensures emergency responses never
                    get blocked by one person's inaction.
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
